import React from 'react';
import { bottomSheet, useBottomSheet } from './BottomSheet';
import './RedeemInfoSheet.css';
import amazonPayLogo from './amazon-pay.svg';

const e = React.createElement;

// Vouchers are issued by hand, so the sheet has to say who to ask --
// otherwise the option reads as something the page can do for you.
const VOUCHER_EMAIL = 'redeem@eventgraphia.com';

// Last resorts, not the source: the rates and the voucher floor are all
// served by the API (`redeem_rates`, `redeem_limits`) so ops own them and
// every surface quotes one figure. These only stand in when a call has
// failed, so the sheet says something rather than a dash or a NaN.
const DEFAULT_BOOKING_RATE = 1;
const DEFAULT_GIFT_CARD_RATE = 0.5;
const DEFAULT_VOUCHER_MIN_COINS = 500;

const formatRupees = (n) =>
  (Math.floor(n * 100) / 100).toLocaleString('en-IN', {
    maximumFractionDigits: 2,
  });

// The rate as a swap the reader can picture: so many coins for so many
// rupees, both whole. A coin worth less than a rupee is quoted as the
// coins a rupee takes -- '2 Coins = ₹1' rather than '₹0.5 per coin', which
// reads as small change and makes the reader do the division.
//
// Only where the coins come out whole. A rate that does not divide into a
// rupee evenly stays the other way round, since rounding the coin count
// would quote a rate nobody is offering.
const rateLabel = (rate) => {
  if (!Number.isFinite(rate) || rate <= 0) return '—';
  if (rate < 1) {
    const perRupee = 1 / rate;
    const whole = Math.round(perRupee);
    if (Math.abs(perRupee - whole) < 0.0001) return `${whole} ${whole === 1 ? 'Coin' : 'Coins'} = ₹1`;
  }
  return `1 Coin = ₹${formatRupees(rate)}`;
};

// A rate the API did not send, or sent as something that is not a number,
// falls back rather than reaching the page as NaN.
const toRate = (value, fallback) => {
  const n = Number(value ?? fallback);
  return Number.isFinite(n) ? n : fallback;
};

/**
 * What one coin is worth, each way it can be spent.
 *
 * Read here and by the rate chip on the balance card, so the fallbacks are
 * stated once and the two cannot drift apart or quote the same rate in two
 * different types.
 */
export const redeemRates = (rates) => ({
  booking: toRate(rates?.booking, DEFAULT_BOOKING_RATE),
  giftCard: toRate(rates?.gift_card, DEFAULT_GIFT_CARD_RATE),
});

/**
 * What each way of redeeming is bounded by, from the API's
 * ``redeem_limits``. Read through the same guard as the rates, so a
 * missing or malformed figure falls back instead of printing NaN.
 *
 * ``giftCardSources`` and its labels are null when the API sent no list,
 * so a failed call quotes the voucher as before rather than as nothing.
 */
export const redeemLimits = (limits) => ({
  giftCardMinCoins: toRate(
    limits?.gift_card_min_coins, DEFAULT_VOUCHER_MIN_COINS
  ),
  giftCardSources: Array.isArray(limits?.gift_card_sources)
    ? limits.gift_card_sources
    : null,
  giftCardSourceLabels: Array.isArray(limits?.gift_card_source_labels)
    ? limits.gift_card_source_labels
    : null,
});

// 'Referral', 'Referral and Coupon', 'Referral, Coupon and Refund'.
const joinLabels = (labels) => (labels.length > 1
  ? `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
  : labels[0]);

// The footnote under the voucher card naming the coins it takes, or null
// when the API sent no list to name.
const voucherFootnote = (labels) => {
  if (labels === null) return null;
  if (labels.length === 0) {
    return '* No EG Coins can go on an Amazon voucher right now.';
  }
  return `* Only ${joinLabels(labels)} coins are applicable for `
    + 'Amazon voucher.';
};

/**
 * Whether ops allow coins on an Amazon voucher at all, or, given a
 * ``source``, coins from that source. Shared with the referral page so
 * its voucher line and this sheet agree.
 */
export const giftCardAllowed = (limits, source) => {
  const { giftCardSources } = redeemLimits(limits);
  if (giftCardSources === null) return true;
  return source
    ? giftCardSources.includes(source)
    : giftCardSources.length > 0;
};

/**
 * The two ways to redeem EG Coins and what the customer's balance is worth
 * each way. Rates come from the wallet API (`redeem_rates`), so a reprice by
 * ops shows here without a release.
 *
 * Built on the shared bottom sheet, so it opens, closes and dismisses exactly
 * as the coupon sheet does.
 */
export const RedeemInfoSheet = ({
  open, onClose, balance, giftCardCoins, rates, limits,
}) => {
  const { closing, requestClose } = useBottomSheet({ open, onClose });

  if (!open) return null;

  // The referral page opens this sheet to explain the rates to someone
  // who may not be logged in, so it passes no balance. Everything that
  // quotes one is left out there rather than printed as a nought.
  const hasBalance = balance !== null && balance !== undefined;
  const coins = Number(balance) || 0;
  const { booking, giftCard } = redeemRates(rates);
  const { giftCardMinCoins, giftCardSourceLabels } = redeemLimits(limits);
  // Only coins from the sources ops allow can go on a voucher, so the
  // voucher card prices those and not the whole balance. An older API
  // that sends no figure is read as the whole balance, as before.
  const voucherCoins = giftCardCoins == null
    ? coins
    : Math.min(Number(giftCardCoins) || 0, coins);
  const footnote = voucherFootnote(giftCardSourceLabels);

  const option = ({ best, icon, title, note, rate, worthCoins = coins }) =>
    e(
      'div',
      { className: `ris-option${best ? ' ris-option--best' : ''}` },
      best ? e('span', { className: 'ris-best-tag' }, 'Best value') : null,
      e('div', { className: 'ris-icon-slot' }, icon),
      e(
        'div',
        { className: 'ris-text' },
        e('p', { className: 'ris-option-title' }, title),
        e('p', { className: 'ris-note' }, note)
      ),
      e(
        'div',
        { className: 'ris-rate' },
        e('p', { className: 'ris-rate-value' }, rateLabel(rate)),
        // What this balance fetches this way; left out at zero, where it
        // would only repeat a nought in every card.
        worthCoins > 0
          ? e(
              'p',
              { className: 'ris-rate-worth' },
              `= ₹${formatRupees(worthCoins * rate)}`
            )
          : null
      )
    );

  return bottomSheet({
    closing,
    requestClose,
    labelledBy: 'ris-title',
    panel: e(
      'div',
      { className: 'rcs-body' },
      e(
        'h2',
        { className: 'rcs-title ris-heading', id: 'ris-title' },
        'Redeem your EG Coins'
      ),
      hasBalance
        ? e(
            'div',
            { className: 'ris-balance' },
            e(
              'span',
              { className: 'ris-balance-coin', 'aria-hidden': 'true' },
              'EG'
            ),
            `${coins.toLocaleString('en-IN')} coins available`
          )
        : null,
      e(
        'div',
        { className: 'ris-options' },
        option({
          best: true,
          icon: e('span', { className: 'ris-icon-coin' }, 'EG'),
          title: 'On an EG booking',
          note: 'Use them on your next installment from your quotation.',
          rate: booking,
        }),
        option({
          icon: e(
            'span',
            { className: 'ris-icon-logo' },
            e('img', { src: amazonPayLogo, alt: 'Amazon Pay' })
          ),
          title: footnote ? 'Amazon gift voucher*' : 'Amazon gift voucher',
          worthCoins: voucherCoins,
          note: e(
            React.Fragment,
            null,
            `Minimum ${giftCardMinCoins.toLocaleString('en-IN')} coins.`,
            e(
              'span',
              { className: 'ris-note-line' },
              'Write to ',
              e(
                'a',
                {
                  className: 'ris-note-link',
                  href: `mailto:${VOUCHER_EMAIL}`,
                },
                VOUCHER_EMAIL
              ),
              ' to redeem.'
            )
          ),
          rate: giftCard,
        })
      ),
      footnote ? e('p', { className: 'ris-footnote' }, footnote) : null,
      e(
        'button',
        { className: 'rcs-primary', type: 'button', onClick: requestClose },
        'Got it'
      )
    ),
  });
};

export default RedeemInfoSheet;
